/**
 * EAA-1843: inline SVG icons for the Phân quyền screens.
 *
 * WHY NOT EMOJI OR TEXT GLYPHS. The first cut used 🔒 / ✓ / ▾ from the keyboard.
 * Those render as whatever the OS decides — the padlock arrives as a colour emoji
 * on one platform and a flat glyph on another — they ignore `currentColor` and the
 * icon size scale, and their baseline sits differently from the text beside them,
 * which is exactly the misalignment that showed up next to the role names.
 *
 * All of these inherit colour via `currentColor` and size via `size`, so a caller
 * styles them by styling the surrounding text. Geometry matches the handoff's own
 * inline SVGs (`permissions.html`).
 */

export interface IconProps {
    size?: number;
    className?: string;
}

/** Built-in role marker — this row cannot be edited. */
export function LockIcon({ size = 13, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            <rect x="4" y="10" width="16" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
    );
}

/** "This role holds the permission." */
export function CheckIcon({ size = 16, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            <path d="m5 12.5 4.5 4.5L19 7" />
        </svg>
    );
}

/** Sort affordance on a column header — a chevron, per the handoff. */
export function CaretIcon({ up = false, size = 14, className }: IconProps & { up?: boolean }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            {up ? <path d="m6 15 6-6 6 6" /> : <path d="m6 9 6 6 6-6" />}
        </svg>
    );
}

/** Download affordance on "Tải về CSV". */
export function DownloadIcon({ size = 15, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 20h16" />
        </svg>
    );
}

/** Filter affordance on "Bộ lọc". */
export function FilterIcon({ size = 14, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            <path d="M4 5h16l-6 7v6l-4 2v-8L4 5z" />
        </svg>
    );
}

/** Magnifier inside the search fields. */
export function SearchIcon({ size = 15, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={1.8} className={className} aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
    );
}

/**
 * "This code gates no endpoint — it only shows/hides a nav item." Frame 62 turns
 * the old "chỉ ẩn/hiện menu" text chip into this icon: the chip was as wide as
 * the permission label beside it, and the fact is a footnote, not a headline.
 */
export function InfoIcon({ size = 14, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8h.01" />
        </svg>
    );
}

/** Unsaved-changes marker on the matrix save bar. */
export function WarnIcon({ size = 14, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"
             className={className} aria-hidden="true" focusable="false">
            <path d="M12 4 2.5 20h19L12 4z" />
            <path d="M12 10v4M12 17h.01" />
        </svg>
    );
}

/**
 * "Some, not all" on a group checkbox, and "this role does not hold it" in the
 * built-in comparison columns. One glyph for both because they are the same
 * statement at different scopes — a dash reads as "not a plain yes" either way.
 */
export function MinusIcon({ size = 12, className }: IconProps) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
             strokeWidth={3} strokeLinecap="round"
             className={className} aria-hidden="true" focusable="false">
            <path d="M6 12h12" />
        </svg>
    );
}
